import { Component, ViewChild, OnInit, AfterViewInit, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { AuthState, DashboardFilterData, NotificationsState, ReportsState } from '@portals/store';
import { IResponseChart, IParseChart } from '@portals/api';
import { finalize, tap } from 'rxjs';
import { BaseComponent } from '@portals/core';
import * as moment from 'moment';
import { environment } from '@portals/environments';
import { ChartData, ChartOptions, ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

interface TobaccoData {
	tobacco_name: string;
	total_bought: number;
	number_of_entries: string;
	total_spent: number;
	locations: string | null;
}

@Component({
	selector: 'portals-tba-bars',
	templateUrl: './tba-bars.component.html',
	styleUrls: ['./tba-bars.component.scss'],
})
export class TbaBarsComponent extends BaseComponent implements OnInit, AfterViewInit {
	@ViewChild(BaseChartDirective) chart?: BaseChartDirective;

	@Input() projectID!: number;
	@Input() currentFilters!: any;
	@Input() timeframe = 'monthly';
	formLoading = false;
	app = environment.app;
	admin = false;

	public barChartOptions: ChartConfiguration['options'] = {
		responsive: true,
		indexAxis: 'y',
		scales: {
			x: {
				stacked: true,
				ticks: {
					callback: function (value) {
						return Math.abs(Number(value));
					},
					color: '#1010E10', // Light gray for better readability on dark background
				},
				grid: {
					color: 'rgba(255, 255, 255, 0.1)', // Subtle grid lines
				},
			},
			y: {
				stacked: true,

				ticks: {
					color: '#101010', // Light gray for better readability on dark background
				},
				grid: {
					color: 'rgba(255, 255, 255, 0.1)', // Subtle grid lines
				},
			},
		},
		plugins: {
			legend: {
				display: true,
				position: 'top',
				labels: {
					color: '#E0E0E0', // Light gray for better readability on dark background
				},
			},
			tooltip: {
				callbacks: {
					label: function (context) {
						let label = context.dataset.label || '';
						if (label) {
							label += ': ';
						}
						if (context.parsed.x !== null) {
							label += Math.abs(context.parsed.x);
						}
						return label;
					},
				},
				backgroundColor: 'rgba(0, 0, 0, 0.7)', // Semi-transparent black for tooltip
				titleColor: '#FFFFFF',
				bodyColor: '#FFFFFF',
			},
		},
	};
	public mainData: TobaccoData[] = [];
	public barChartType: ChartType = 'bar';
	public barChartData: ChartData<'bar'> = {
		labels: [],
		datasets: [],
	};

	constructor(private reports: ReportsState, private authState: AuthState, private cdr: ChangeDetectorRef) {
		super();
		this.admin = ['super_admin', 'aclu_admin'].includes(this.authState.data.role.type);
	}

	ngOnInit() {
		// Initialize chart data with empty arrays
		this.barChartData = {
			labels: [],
			datasets: [
				{
					data: [],
					label: 'Total Bought Ratio',
					backgroundColor: 'rgba(102, 204, 255, 1)',
					borderColor: 'rgba(102, 204, 255, 1)',
					borderWidth: 1,
				},
				{
					data: [],
					label: 'Total Spent Ratio',
					backgroundColor: 'rgba(255, 179, 71, 1)',
					borderColor: 'rgba(255, 179, 71, 1)',
					borderWidth: 1,
				},
			],
		};
	}

	ngAfterViewInit() {
		this.getChart(this.timeframe, this.currentFilters);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['currentFilters']) {
			const previousValue = changes['currentFilters'].previousValue;
			const currentValue = changes['currentFilters'].currentValue;
			if (!this.isEqual(previousValue, currentValue)) {
				this.getChart(this.timeframe, currentValue);
			}
		}
	}

	private isEqual(obj1: any, obj2: any): boolean {
		return JSON.stringify(obj1) === JSON.stringify(obj2);
	}

	updateChartData(data: TobaccoData[]) {
		const totalQuantity = data.reduce((sum, item) => sum + item.total_bought, 0);
		const totalSpent = data.reduce((sum, item) => sum + item.total_spent, 0);
		let currLabels = [];
		let dsOne = [];
		let dsTwo = [];
		for (let i = 0; i < data.length; i++) {
			const item = data[i];
			currLabels.push(item.tobacco_name);
			dsOne.push((item.total_bought / totalQuantity) * 100);
			dsTwo.push((item.total_spent / totalSpent) * 100);
		}
		this.barChartData.labels = currLabels;
		this.barChartData.datasets[0].data = dsOne;
		this.barChartData.datasets[1].data = dsTwo;

		if (this.chart) {
			this.chart.update();
		}

		this.cdr.detectChanges();
	}

	async getChart(timeframe: string, filters: any) {
		this.formLoading = true;
		this.subscribe(
			this.reports.mostSoldTobacco(this.currentFilters).pipe(
				tap((data: any) => {
					this.tobaccoData = data.allData;
					this.updateChartData(data.allData);
				}),
				finalize(() => {
					this.formLoading = false;
				})
			)
		);
	}

	changeTimeframe(event: any) {
		this.timeframe = event.target.value;
		this.getChart(this.timeframe, this.currentFilters);
	}
}
